import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";
import { ChevronLeft, MapPin, Search, UserPlus } from "lucide-react";

// Demand, recruitment leads, and whether search is actually answering people —
// the three things that tell us what to go build supply for.

type DemandRow = {
  query: string;
  asks: number;
  people: number;
  matched: number;
  zips: string[] | null;
  cities: string[] | null;
  last_asked: string;
};
type LeadRow = {
  id: string;
  maker_name: string;
  maker_contact: string | null;
  note: string | null;
  status: string;
  created_at: string;
  suggested_by: string | null;
};
type QueryRow = { query: string; times: number; results: number; clicked?: number; last_seen?: string };

const LEAD_NEXT: Record<string, { label: string; status: string }[]> = {
  new: [
    { label: "Contacted", status: "contacted" },
    { label: "Not a fit", status: "declined" },
  ],
  contacted: [
    { label: "Joined", status: "joined" },
    { label: "Not a fit", status: "declined" },
  ],
};

function when(value: string): string {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString();
}

export default function AdminDemand() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery<{
    demand: DemandRow[];
    leads: LeadRow[];
    unclicked: QueryRow[];
    topQueries: QueryRow[];
  }>({
    queryKey: ["/api/admin/demand"],
    queryFn: async () => {
      const res = await fetch("/api/admin/demand", { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
  });

  const setStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const res = await fetch(`/api/admin/maker-leads/${id}/status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error(await res.text());
    },
    onSuccess: () => {
      // staleTime is Infinity globally, so an invalidate is the only thing that
      // refetches this (trap #1).
      queryClient.invalidateQueries({ queryKey: ["/api/admin/demand"] });
    },
    onError: () => toast({ description: "Couldn't update that lead." }),
  });

  return (
    <div className="min-h-screen bg-background pb-20">
      <div className="border-b bg-card">
        <div className="container mx-auto flex items-center gap-3 px-4 py-3">
          <Link href="/admin" className="text-muted-foreground">
            <ChevronLeft className="h-5 w-5" />
          </Link>
          <h1 className="text-lg font-semibold">Demand &amp; leads</h1>
        </div>
      </div>

      <div className="container mx-auto space-y-8 px-4 py-6">
        {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}

        {/* What people asked for that nobody sells. The zips are the point:
            they say where to go recruiting. */}
        <section>
          <h2 className="mb-1 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Wanted, nobody sells it
          </h2>
          <p className="mb-3 text-xs text-muted-foreground">
            Grouped by what was asked for. Use the ZIPs when recruiting makers.
          </p>
          {data && data.demand.length === 0 && (
            <p className="text-sm text-muted-foreground">No requests yet.</p>
          )}
          <div className="space-y-2">
            {data?.demand.map((row) => (
              <Card key={row.query}>
                <CardContent className="flex items-start justify-between gap-3 p-3">
                  <div className="min-w-0">
                    <div className="font-medium">{row.query}</div>
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      {row.people} {row.people === 1 ? "person" : "people"} · {row.asks}{" "}
                      {row.asks === 1 ? "ask" : "asks"} · last {when(row.last_asked)}
                      {row.matched > 0 && ` · ${row.matched} filled`}
                    </div>
                    {(row.zips?.length || row.cities?.length) && (
                      <div className="mt-1 flex items-center gap-1 text-xs text-primary">
                        <MapPin className="h-3 w-3" />
                        {(row.cities?.length ? row.cities : row.zips ?? []).join(", ")}
                      </div>
                    )}
                  </div>
                  <div className="shrink-0 text-2xl font-bold text-primary">{row.asks}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Neighbors a buyer vouched for — the warmest outreach list we have. */}
        <section>
          <h2 className="mb-1 flex items-center gap-1.5 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            <UserPlus className="h-3.5 w-3.5" />
            Maker leads
          </h2>
          <p className="mb-3 text-xs text-muted-foreground">
            Neighbors a buyer named. Work them top to bottom.
          </p>
          {data && data.leads.length === 0 && (
            <p className="text-sm text-muted-foreground">No leads yet.</p>
          )}
          <div className="space-y-2">
            {data?.leads.map((lead) => (
              <Card key={lead.id} className={lead.status === "new" ? "border-primary/40" : undefined}>
                <CardContent className="p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium">{lead.maker_name}</div>
                      {lead.maker_contact && (
                        <div className="text-xs text-primary">{lead.maker_contact}</div>
                      )}
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        {lead.note}
                        {lead.suggested_by ? ` · from ${lead.suggested_by}` : ""} · {when(lead.created_at)}
                      </div>
                    </div>
                    <span className="shrink-0 rounded-full border border-border px-2 py-0.5 text-[10px] uppercase text-muted-foreground">
                      {lead.status}
                    </span>
                  </div>
                  {LEAD_NEXT[lead.status] && (
                    <div className="mt-2 flex gap-2">
                      {LEAD_NEXT[lead.status].map((action) => (
                        <Button
                          key={action.status}
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          disabled={setStatus.isPending}
                          onClick={() => setStatus.mutate({ id: lead.id, status: action.status })}
                        >
                          {action.label}
                        </Button>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Answered, but nothing was worth opening — the honest accuracy check. */}
        <section>
          <h2 className="mb-1 flex items-center gap-1.5 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            <Search className="h-3.5 w-3.5" />
            Results nobody opened
          </h2>
          <p className="mb-3 text-xs text-muted-foreground">
            We returned something and the searcher tapped none of it. Usually means the matches were wrong.
          </p>
          {data && data.unclicked.length === 0 && (
            <p className="text-sm text-muted-foreground">Nothing here — good sign.</p>
          )}
          <div className="divide-y rounded-xl border border-border">
            {data?.unclicked.map((row) => (
              <div key={row.query} className="flex items-center justify-between px-3 py-2 text-sm">
                <span className="min-w-0 truncate">{row.query}</span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {row.times}× · {row.results} results
                </span>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="mb-1 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Most searched
          </h2>
          <p className="mb-3 text-xs text-muted-foreground">
            What people look for, and how often it led to a tap.
          </p>
          <div className="divide-y rounded-xl border border-border">
            {data?.topQueries.map((row) => (
              <div key={row.query} className="flex items-center justify-between px-3 py-2 text-sm">
                <span className="min-w-0 truncate">{row.query}</span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {row.times}× · {row.clicked ?? 0} opened
                </span>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
